import type { DocumentoCadastroParte } from "@atlas/sdk";
import { FormGrid } from "@atlas/ui/web";
import { DocumentoPreview } from "./DocumentoPreview";

// Bloco "Documentos do cadastro" dos modais de banco e prefeitura.
//
// Identico nas duas telas a menos da funcao que baixa os bytes
// (admin.bancoDocumentoBlob vs admin.prefeituraDocumentoBlob), entao vive aqui
// — mesmo padrao de CadastroPessoas e AcessoMaster.
//
// As chaves (`docFrenteKey`/`docVersoKey`/`selfieKey`) sao gravadas pelo cadastro
// do site institucional; cadastro criado pela propria averbadora nao tem nenhuma.

/** Os tres anexos na ordem do Passo 5. Uma lista so, pra o bloco e o pill da
 *  tabela nunca discordarem sobre o que conta como envio completo. */
const ANEXOS: { parte: DocumentoCadastroParte; label: string }[] = [
  { parte: "frente", label: "Frente" },
  { parte: "verso", label: "Verso" },
  { parte: "selfie", label: "Selfie" },
];

function anexosPresentes(keys: AnexosKeys): DocumentoCadastroParte[] {
  return ANEXOS.filter(({ parte }) => keys[parte === "selfie" ? "selfieKey" : parte === "frente" ? "docFrenteKey" : "docVersoKey"]).map((a) => a.parte);
}

type AnexosKeys = {
  docFrenteKey?: string;
  docVersoKey?: string;
  selfieKey?: string;
};

export function DocumentosCadastro({
  docFrenteKey,
  docVersoKey,
  selfieKey,
  carregar,
  /** Cadastro veio da esteira do site (tem protocolo). Controla o estado vazio:
   *  faltar documento ali e' informacao relevante pro operador, enquanto num
   *  cadastro criado na averbadora o bloco todo simplesmente nao se aplica. */
  veioDoSite,
}: AnexosKeys & {
  carregar: (parte: DocumentoCadastroParte) => Promise<Blob>;
  veioDoSite: boolean;
}) {
  const keys = { docFrenteKey, docVersoKey, selfieKey };
  const presentes = anexosPresentes(keys);
  const faltando = ANEXOS.filter((a) => !presentes.includes(a.parte));
  if (presentes.length === 0 && !veioDoSite) return null;

  return (
    <div style={{ padding: 14, background: "var(--bg-elev-2)", borderRadius: 10, border: "1px solid var(--border)" }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase", marginBottom: 10 }}>
        Documentos do cadastro
      </div>

      {/* Sem os arquivos o bloco precisa dizer isso em voz alta. Escondido, o
          operador nao consegue distinguir "o solicitante nao anexou" de "a tela
          nao esta mostrando" — e aprova sem conferir documento nenhum. */}
      {presentes.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--warn-500)" }}>
          ⚠ Nenhum documento foi anexado neste cadastro.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>
            Enviados pelo solicitante no cadastro do site. Clique para ampliar.
          </div>
          {/* cols=2 e nao 3: no modal de 640px a miniatura de um documento de
              identidade em 1/3 da largura fica ilegivel. Frente/verso pareiam e a
              selfie cai na linha de baixo. */}
          <FormGrid cols={2}>
            {ANEXOS.filter((a) => presentes.includes(a.parte)).map(({ parte, label }) => (
              <DocumentoPreview key={parte} label={label} parte={parte} carregar={carregar} />
            ))}
          </FormGrid>
          {/* Nomear o que falta, e nao so avisar que falta: e' a diferenca entre o
              operador saber que precisa pedir a selfie e ele ir procurar. */}
          {faltando.length > 0 ? (
            <div style={{ fontSize: 11, color: "var(--warn-500)", marginTop: 8 }}>
              ⚠ Envio incompleto — falta {faltando.map((a) => a.label.toLowerCase()).join(", ")}.
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

/** Indicador de anexos pra coluna da tabela — mesma leitura em bancos e
 *  prefeituras. Fica ao lado do bloco pra as duas visoes nao divergirem. */
export function DocumentosPill(keys: AnexosKeys) {
  const presentes = anexosPresentes(keys);
  if (presentes.length === ANEXOS.length) {
    return <span style={{ color: "var(--emerald-500)" }} title="Frente, verso e selfie anexados">✓ completo</span>;
  }
  if (presentes.length > 0) {
    return (
      <span style={{ color: "var(--warn-500)" }} title={`Envio incompleto: ${presentes.length} de ${ANEXOS.length}`}>
        ⚠ {presentes.length}/{ANEXOS.length}
      </span>
    );
  }
  return <span style={{ color: "var(--text-dim)" }}>—</span>;
}
